import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { resolveHeroBadge } from './resolveHeroBadge';
import { SEAL_ALT_FALLBACK, type HeroResolved } from './types';
import { PromoBadge } from '@/components/PromoBadge';
import { SlotRenderer } from '@/components/SlotRenderer';
import { resolvePrice } from '@/lib/pricing/resolvePrice';
import { hasRichTextContent } from '@/lib/render/hasRichTextContent';
import { imageAlt } from '@/lib/render/imageAlt';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { resolveMediaPlaceholder } from '@/lib/render/resolveMediaPlaceholder';
import { substituteHref } from '@/lib/render/substituteHref';
import { Text } from '@/lib/render/Text';
import { useRuntimeConfig } from '@/lib/runtime-config/RuntimeConfigProvider';
import { HeroCardSlot } from '@/src/widgets/hero-cards/HeroCardSlot';
import {
  heroCardHasSearch,
  heroCardIsFullWidth,
  isHeroCardBlock,
} from '@/src/widgets/hero-cards/types';
import {
  LocationCard,
  type LocationCardConfig,
} from '@/src/widgets/location-card/LocationCardComponent';
import { OlcFormComponent } from '@/src/widgets/olc-form/OlcFormComponent';
import type { OlcFormConfig } from '@/src/widgets/olc-form/types';

/**
 * Slot geometry for the Hero's content card. It has to sit on the card's own
 * outermost element, as a direct flex child of the row below — which is why the
 * cards are rendered structurally here rather than through `SlotRenderer`,
 * whose fixed wrapper would bury it two divs deep.
 */
const SLOT_CLASS = 'order-2 lg:order-1 lg:max-w-[360px] lg:shrink-0';

/**
 * Geometry for the cards that take the whole width instead of the narrow slot
 * (`HERO_CARD_TYPES_FULL_WIDTH`). Centered, capped short of the hero's own
 * `max-w-6xl`, and pulled up so the panel overlaps the image's lower edge —
 * the Location Search Result page's design (10.02.11).
 */
const FULL_WIDTH_SLOT_CLASS = 'rounded-t-2xl px-4 pt-7 pb-10 md:px-6 md:pt-10';

/**
 * The tag `resolveTagBag` puts in the bag for `/nachhilfe/{slug}/` and for
 * nothing else — the Standort page, and the only place the Location Card's
 * name outranks the Hero's headline. Spelled out here rather than imported
 * from `lib/resolve-tag-bag`, which reaches for `next/headers` and cannot be
 * pulled into a client component.
 */
const LOCATION_PAGE_TAG = 'type:location';

/**
 * What the base `h1` rule in `globals.css` gives the Hero's title and the base
 * `p` rule does not: its letter-spacing, and no bottom margin (`p` carries a
 * 16px one). Applied to the title whichever element it ends up being, so the
 * two render identically — on the `h1` these only restate its computed values.
 */
const TITLE_BASE_CLASS = 'mb-0 tracking-[-0.3px] md:tracking-[-0.56px]';

export function HeroComponent({ resolved }: { resolved: HeroResolved }) {
  const {
    substitutePlaceholders: sub,
    entities,
    imageUrl,
    tagBag,
  } = useRenderContext();
  const {
    title,
    backgroundImage,
    content = [],
    badgeText,
    badgeRichText,
    seal,
  } = resolved;
  const cardBlock = content.find(
    (b): b is LocationCardConfig => b.componentType === 'widgets.location-card',
  );
  // The `content` zone is capped at `max: 1`, so an Editor picks exactly one
  // card and no precedence logic is needed. The `!cardBlock` guard only covers
  // a legacy entry that predates the cap: it keeps rendering exactly as it does
  // today (SH-375).
  const olcBlock = !cardBlock
    ? content.find(
        (b): b is OlcFormConfig => b.componentType === 'widgets.olc-form',
      )
    : undefined;
  // One card per Hero, and the Editor picks which (SH-312 Q5). The CMS enforces
  // it with `max: 1` on the zone; here any card beyond the first is ignored.
  const heroCardBlock = content.find(isHeroCardBlock);
  // A few cards leave the narrow slot and take the full width, centered, with
  // the Hero's own h1 moved inside the panel — today only the Location Search
  // Result page's card (10.02.11).
  const fullWidthCard = heroCardBlock
    ? heroCardIsFullWidth(heroCardBlock)
    : false;
  // A card with a location search opens an autocomplete dropdown that reaches
  // past the Hero's fixed height. `lg:overflow-hidden` would clip it — and no
  // z-index can help, because the section clips rather than stacks (it has no
  // z-index of its own). So the Hero stops clipping vertically for exactly
  // those cards, and keeps clipping horizontally so a full-bleed background
  // can never produce a page-wide horizontal scrollbar.
  const slotOverflows = heroCardBlock
    ? heroCardHasSearch(heroCardBlock)
    : false;
  const loc = entities.location;
  // The card slot's other occupant: the magazine's Article Card, rendered
  // through `SlotRenderer` so it gets its `resolved` payload out of the same
  // `ResolvedMapProvider` every other lookup widget reads (spec 10.02.05
  // § AC 2 — no magazine-specific hero widget exists). A Location Card with a
  // location in context still wins the slot.
  const featuredCards = content.filter(
    (b) => b.componentType === 'magazine.article-card',
  );
  const showFeaturedCard = featuredCards.length > 0 && !(cardBlock && loc);
  // `backgroundOverride` holds an entity-image token like {city.image}:
  // when it resolves, that image beats the block's own backgroundImage —
  // an entity without the referenced image falls back to backgroundImage.
  const background =
    resolveMediaPlaceholder(resolved.backgroundOverride, entities) ??
    backgroundImage;
  const bgUrl = imageUrl(background);
  // A hero photo is a backdrop and stays unlabelled while the media carries no
  // alt text. Once an editor types one in Strapi the picture is content, so it
  // gets its accessible name — substituted, like every editor-typed string.
  const bgAlt = sub(imageAlt(background));
  const sealUrl = seal ? imageUrl(seal) : '';
  // The seal is meaningful content, not decoration, so it needs an alt. Editors
  // supply it on the Strapi media; SEAL_ALT_FALLBACK covers an empty one.
  const sealAlt = sub(imageAlt(seal, SEAL_ALT_FALLBACK));

  // CTA fields: use CMS-configured values if set, otherwise fall back to the
  // page shell's resolved phone pair. That pair is the backend's 70.07 chain
  // (`resolvedPhone`/`resolvedMatelsoPhoneLabel`, surfaced on the `domain`
  // entity): the Standort's own number, or the Domain Setting's when it has
  // none. Reading `loc.phone` alone left the 269 Standorte without a number
  // with no usable CTA at all.
  //
  // Taking both halves from the same source keeps the hard pair intact — a
  // Standort with its own number never borrows the domain's Matelso label,
  // which would print a number different from the one being dialled.
  const shellPhone = entities.domain;
  const fallbackPhone = shellPhone?.phone;
  const fallbackLabel = shellPhone?.matelsoPhoneLabel || fallbackPhone;

  const primaryCtaLabel =
    (cardBlock ? sub(cardBlock.primaryCtaLabel) : '') || fallbackLabel || '';
  // An unresolved `{location.*}` token is a literal, non-empty string, so it
  // would win this `||` chain and reach the card as an href that dials nothing
  // — which is how a phoneless Standort ended up with no usable CTA. Treating
  // it as unset lets the shell pair take over. Only the href is blanked: a
  // token left in the *label* stays visible on purpose, as the signal to the
  // editor that the field behind it is empty.
  const primaryCtaHref =
    (cardBlock ? substituteHref(cardBlock.primaryCtaHref, sub) : '') ||
    fallbackPhone ||
    '';
  const secondaryCtaLabel =
    (cardBlock ? sub(cardBlock.secondaryCtaLabel) : '') || '';
  const secondaryCtaHref = cardBlock
    ? substituteHref(cardBlock.secondaryCtaHref, sub)
    : '';

  // On a Standort page the Location Card's name — "Schülerhilfe Aalen" — is
  // what the URL is about, while the Hero's headline is campaign copy every
  // location shares. So there the card takes the h1 and the Hero's title steps
  // down to a paragraph (SH-469). Everywhere else the Hero keeps it.
  //
  // Both halves hang off one condition on purpose: swapping them apart is how
  // a page ends up with two h1s, or with none. The card has to be rendering
  // *and* have a name — `cardBlock && loc` is the same guard the slot below
  // renders on, and a blank name would hand the h1 to an empty element.
  const locationCardTitle =
    cardBlock && loc ? sub(cardBlock.locationTitle) : '';
  const locationCardOwnsH1 =
    tagBag.includes(LOCATION_PAGE_TAG) && Boolean(locationCardTitle);
  const titleAs = locationCardOwnsH1 ? 'p' : 'h1';

  // The rich-text badge wins whenever an editor has filled it in (SH-424). It
  // carries its own tiers and resolves no price, so a page without a location
  // — where `resolveHeroBadge` can only ever hide the badge — can still show
  // one. `badgeText` stays the fallback for every badge authored so far.
  const { brand } = useRuntimeConfig();
  const badgeRich = hasRichTextContent(badgeRichText)
    ? badgeRichText
    : undefined;
  const badge = badgeRich
    ? null
    : resolveHeroBadge(badgeText, sub, (placeholder) =>
        resolvePrice(loc, placeholder, brand),
      );

  // A full-width card gets its own hero geometry: the image is a band across
  // the top rather than a backdrop behind everything, and the section carries
  // the card's own cream below it, so the panel and the page merge into one
  // band instead of floating on the photo (10.02.11). The card itself is a
  // normal Hero Card — only the frame around it differs.
  if (fullWidthCard && heroCardBlock) {
    return (
      <section className="bg-secondary-lighter relative">
        <div className="relative h-[65vw] max-h-140 w-full md:h-[46vw]">
          {bgUrl && (
            <Image
              src={bgUrl}
              alt={bgAlt}
              fill
              className="object-cover object-center"
              priority
              loading="eager"
              fetchPriority="high"
            />
          )}
          {/* Only the bottom gradient survives: the top one exists to keep
              white text legible over the image, and no text sits there now. */}
          <div className="absolute bottom-0 h-[40%] w-full bg-gradient-to-b from-transparent to-black opacity-90 md:h-[30%]" />
        </div>

        <div className="relative mx-auto -mt-16 max-w-4xl px-4 md:-mt-24 lg:px-9">
          <HeroCardSlot
            block={heroCardBlock}
            className={FULL_WIDTH_SLOT_CLASS}
            heading={
              title ? (
                <Text
                  as={titleAs}
                  field="title"
                  className={classNames(
                    TITLE_BASE_CLASS,
                    'text-secondary text-base leading-tight font-medium md:text-2xl',
                  )}
                >
                  {title}
                </Text>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className={classNames(
        'relative lg:h-[55vw] lg:max-h-screen xl:h-[50vw]',
        slotOverflows
          ? 'lg:overflow-x-clip lg:overflow-y-visible'
          : 'lg:overflow-hidden',
      )}
    >
      <div className="relative h-[110vw] w-full md:h-[60vw] lg:absolute lg:inset-0 lg:h-auto">
        {bgUrl && (
          <Image
            src={bgUrl}
            alt={bgAlt}
            fill
            className="object-cover object-[60%]"
            priority
            loading="eager"
            fetchPriority="high"
          />
        )}
        <div className="absolute top-0 h-[40%] w-full bg-gradient-to-b from-black to-transparent opacity-50 md:h-[20%]" />
        <div className="absolute bottom-0 h-[40%] w-full bg-gradient-to-b from-transparent to-black opacity-90 md:h-[30%]" />
      </div>

      <div className="relative mx-auto -mt-32 flex max-w-6xl flex-col px-4 pb-8 md:-mt-40 lg:mt-0 lg:h-full lg:flex-row lg:items-end lg:justify-between lg:gap-8 lg:px-9 lg:pt-56 lg:pb-0">
        <div
          className={classNames(
            'relative order-1 mb-6 w-full lg:mb-0 lg:flex-1 lg:pb-8',
            // The Article Card sits right, per the magazine design; the
            // Location Card sits left, so the title column swaps sides.
            showFeaturedCard ? 'lg:order-1' : 'lg:order-2',
          )}
        >
          {(title || sealUrl) && (
            // Flex row so an optional seal sits right of the title. With no
            // seal, the h1 is the single `grow` child and fills the row —
            // pixel-identical to the previous block-level markup.
            <div className="relative z-10 flex items-center gap-4">
              {title && (
                <Text
                  as={titleAs}
                  field="title"
                  className={classNames(
                    TITLE_BASE_CLASS,
                    'grow text-center text-2xl leading-tight font-semibold text-white md:px-36 md:text-5xl md:leading-none md:font-bold lg:pr-6 lg:pb-4 lg:pl-0 lg:text-left lg:text-6xl',
                  )}
                >
                  {title}
                </Text>
              )}
              {sealUrl && (
                <Image
                  src={sealUrl}
                  alt={sealAlt}
                  width={160}
                  height={160}
                  // Seals are portrait; object-right keeps the image flush with
                  // the row's right edge, under the badge (SH-454).
                  className="h-22 w-22 shrink-0 object-contain object-right md:h-40 md:w-40"
                />
              )}
            </div>
          )}
          {(badgeRich || badge) && (
            <PromoBadge
              prefix={badge?.prefix ?? ''}
              price={badge?.price ?? ''}
              unit={badge?.unit ?? ''}
              richText={badgeRich}
              className={classNames(
                'absolute z-0 rotate-[12deg] md:top-0',
                // The badge normally dips into the title row. A seal sits at
                // the right end of that row, right under the badge, so with a
                // seal the badge clears the row entirely and floats above it,
                // right-aligned with the seal, as on the legacy site (SH-454).
                sealUrl
                  ? 'top-0 right-0 -translate-y-[calc(100%+1rem)] md:-translate-y-[calc(100%+1.5rem)]'
                  : '-top-10 right-8 -translate-y-[35%] md:-right-7 md:-translate-y-[80%]',
              )}
            />
          )}
        </div>

        {heroCardBlock ? (
          <HeroCardSlot block={heroCardBlock} className={SLOT_CLASS} />
        ) : olcBlock ? (
          <OlcFormComponent resolved={olcBlock} className={SLOT_CLASS} />
        ) : cardBlock && loc ? (
          <LocationCard
            titleAs={locationCardOwnsH1 ? 'h1' : 'h2'}
            data={{
              locationTitle: locationCardTitle,
              street: loc.street ?? '',
              zipCode: loc.zipCode ?? '',
              cityName: loc.cityName ?? '',
              primaryCta: {
                label: primaryCtaLabel,
                href: primaryCtaHref,
                tracking: cardBlock?.primaryCtaTracking,
              },
              promoText: sub(cardBlock.promoText),
              secondaryCta: {
                label: secondaryCtaLabel,
                href: secondaryCtaHref,
                tracking: cardBlock?.secondaryCtaTracking,
              },
            }}
            className={SLOT_CLASS}
          />
        ) : showFeaturedCard ? (
          <div className="order-2 w-full lg:order-2 lg:max-w-[440px] lg:shrink-0 lg:self-end">
            <SlotRenderer blocks={featuredCards} zoneName="content" />
          </div>
        ) : (
          <div
            aria-hidden
            className="order-2 h-16 w-full md:h-40 lg:order-1 lg:h-0 lg:max-w-[360px] lg:shrink-0"
          />
        )}
      </div>
    </section>
  );
}
